@if (initialized) {
    <div class="cn-activity-inbox">

        <!-- ===================== KOPFZEILE: SCOPE + AKTUALISIEREN + TASTATURHILFE ===================== -->
        <div class="flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
            <div class="flex align-items-center gap-2 flex-wrap">
                <i class="fa-regular fa-inbox text-xl text-primary"></i>
                <span class="text-lg font-medium">Provisions-Eingangskorb</span>
                @if (period) {
                    <p-tag severity="info" [value]="'Periode ' + period"></p-tag>
                }
            </div>
            <div class="flex align-items-center gap-2 flex-wrap">
                <!-- Tastatur-Legende -->
                <span class="text-600 cursor-help"
                      [pTooltip]="shortcutLegend"
                      tooltipPosition="bottom"
                      tooltipStyleClass="cn-shortcut-tooltip">
                    <i class="fa-regular fa-keyboard mr-1"></i>
                    <span class="text-sm">Tastaturkürzel</span>
                </span>
                <ng-template #shortcutLegend>
                    <div class="text-left">
                        <div class="font-bold mb-1">Tastaturkürzel</div>
                        <div class="text-xs mb-1">Aktiv, wenn die Tabelle den Fokus hat.</div>
                        <div>↑ / ↓ — Zeile wechseln</div>
                        <div>Bild ↑ / Bild ↓ — Seite wechseln</div>
                        <div>Leertaste — Auswahl umschalten</div>
                        <div>a — alle offenen auswählen</div>
                        <div>q / Enter — quittieren</div>
                        <div>Esc — Auswahl aufheben bzw. abbrechen</div>
                    </div>
                </ng-template>
                <p-button label="Aktualisieren"
                          icon="fa-regular fa-arrows-rotate"
                          severity="secondary"
                          size="small"
                          [outlined]="true"
                          [loading]="listLoading"
                          (onClick)="manualRefresh()">
                </p-button>
            </div>
        </div>

        <!-- ===================== KPI-KACHELN (ZEITRAUM-BEZOGENE UNTER-ZÄHLER) ===================== -->
        <div class="grid mb-1">
            <div class="col-6 md:col-4">
                <div class="cn-inbox-kpi surface-0 border-1 surface-border border-round p-3 h-full">
                    <div class="text-sm text-600 mb-1">Offen (im gewählten Zeitraum)</div>
                    @if (countsLoading) {
                        <p-skeleton height="2.5rem" width="60%"></p-skeleton>
                    } @else {
                        <div class="text-3xl font-bold text-orange-500">
                            {{ (openCount ?? 0) | number:'1.0-0':'de' }}
                        </div>
                    }
                </div>
            </div>

            <div class="col-6 md:col-4">
                <div class="cn-inbox-kpi surface-0 border-1 surface-border border-round p-3 h-full">
                    <div class="text-sm text-600 mb-1">Quittiert (im gewählten Zeitraum)</div>
                    @if (countsLoading) {
                        <p-skeleton height="2.5rem" width="60%"></p-skeleton>
                    } @else {
                        <div class="text-3xl font-bold text-green-500">
                            {{ (acknowledgedCount ?? 0) | number:'1.0-0':'de' }}
                        </div>
                    }
                </div>
            </div>
        </div>
        <div class="text-xs text-500 mb-3">
            <i class="fa-regular fa-circle-info mr-1"></i>
            Unter-Zähler des gewählten Zeitraums — die Gesamtzahlen zeigt die Controlling-Leiste oben.
        </div>

        <!-- ===================== TEXTSUCHE ===================== -->
        <div class="mb-3 w-full md:w-25rem">
            <div class="relative">
                <i class="fa-regular fa-magnifying-glass absolute text-600"
                   style="left: 0.75rem; top: 50%; transform: translateY(-50%);"></i>
                <input type="text"
                       pInputText
                       class="w-full pl-5"
                       [(ngModel)]="searchTerm"
                       (ngModelChange)="onSearchChange()"
                       placeholder="Suche nach VSNR, Gesellschaft oder Tarif…"/>
                @if (searchTerm) {
                    <i class="fa-regular fa-xmark absolute cursor-pointer text-600"
                       style="right: 0.75rem; top: 50%; transform: translateY(-50%);"
                       (click)="clearSearch()"></i>
                }
            </div>
            <!-- TM-1000001915 (4): with server-side paging the free-text search narrows only the currently
                 loaded page (VSNR / Tarif / Kunde are resolved fields, not server-filterable columns). -->
            <div class="text-xs text-500 mt-1">
                <i class="fa-regular fa-circle-info mr-1"></i>
                Die Suche filtert die aktuelle Seite. Für Treffer auf weiteren Seiten bitte blättern oder die Status-/Art-Filter nutzen.
            </div>
        </div>

        <!-- ===================== STATUS-FILTER (UNTER-FILTER) ===================== -->
        <div class="flex align-items-center gap-2 mb-2 flex-wrap">
            <span class="text-sm text-600 mr-1">Status:</span>
            <p-button label="Alle"
                      [outlined]="activeStatusFilter !== 'ALL'"
                      size="small"
                      (onClick)="setStatusFilter('ALL')">
            </p-button>
            <p-button label="Offen"
                      severity="warn"
                      [outlined]="activeStatusFilter !== 'OPEN'"
                      size="small"
                      (onClick)="setStatusFilter('OPEN')">
            </p-button>
            <p-button label="Quittiert"
                      severity="success"
                      [outlined]="activeStatusFilter !== 'ACKNOWLEDGED'"
                      size="small"
                      (onClick)="setStatusFilter('ACKNOWLEDGED')">
            </p-button>
        </div>

        <!-- ===================== ART-FILTER (UNTER-FILTER) ===================== -->
        <div class="flex align-items-center gap-2 mb-3 flex-wrap">
            <span class="text-sm text-600 mr-1">Art:</span>
            <p-button label="Alle"
                      [outlined]="activeKindFilter !== 'ALL'"
                      size="small"
                      (onClick)="setKindFilter('ALL')">
            </p-button>
            <p-button label="Neuvertrag"
                      [outlined]="activeKindFilter !== 'NEW_COMMISSION'"
                      size="small"
                      (onClick)="setKindFilter('NEW_COMMISSION')">
            </p-button>
            <p-button label="Storno"
                      [outlined]="activeKindFilter !== 'STORNO'"
                      size="small"
                      (onClick)="setKindFilter('STORNO')">
            </p-button>
            <p-button label="Abgleich"
                      [outlined]="activeKindFilter !== 'RECONCILIATION'"
                      size="small"
                      (onClick)="setKindFilter('RECONCILIATION')">
            </p-button>
        </div>

        <!-- ===================== BULK-AKTION + BULK-ERGEBNIS ===================== -->
        <div class="flex align-items-center justify-content-between flex-wrap gap-2 mb-2">
            <div class="flex align-items-center gap-3 flex-wrap">
                <span class="text-sm text-600">
                    {{ selectedRows.length | number:'1.0-0':'de' }} ausgewählt
                </span>
                @if (bulkResultMessage) {
                    <span class="text-sm font-medium text-green-600">
                        <i class="fa-regular fa-circle-check mr-1"></i>{{ bulkResultMessage }}
                    </span>
                }
            </div>
            <p-button [label]="(selectedRows.length | number:'1.0-0':'de') + ' quittieren'"
                      icon="fa-regular fa-check-double"
                      severity="success"
                      size="small"
                      [disabled]="selectedRows.length === 0 || acknowledgeBusy"
                      (onClick)="openConfirmBulk()">
            </p-button>
        </div>

        <!-- ===================== LISTEN-FEHLER ===================== -->
        @if (listError) {
            <p-message severity="error" class="block mb-3" [text]="listError"></p-message>
        }

        <!-- ===================== AKTIVITÄTEN-TABELLE ===================== -->
        <!-- TM-1000001915 (4): the lazy p-table must ALWAYS render so it can fire its initial (onLazyLoad)
             and load page 0. Loading + empty states are handled by the table itself ([loading] overlay and
             the "emptymessage" template), not by an outer @if that would prevent the first load. -->
        <!-- TM-1000001915 (d): focusable region so the keyboard shortcuts only act when the operator
             has actually clicked / tabbed into the inbox table, never globally. -->
        <div class="cn-inbox-table-region"
             tabindex="0"
             role="group"
             aria-label="Provisions-Eingangskorb Tabelle (Tastaturkürzel aktiv)">
            <!-- TM-1000001915 (4): server-side (lazy) paging. The table loads ONE page at a time via
                 (onLazyLoad); [totalRecords] comes from the paged envelope's totalElements. The KPI tiles
                 above are NOT derived from this page — they come from GET /cn/activity/count. -->
            <p-table [value]="rows"
                     styleClass="p-datatable-sm"
                     [scrollable]="true"
                     [lazy]="true"
                     [paginator]="true"
                     [rows]="rowsPerPage"
                     [first]="first"
                     [totalRecords]="totalRecords"
                     [loading]="listLoading"
                     [rowsPerPageOptions]="[10, 25, 50]"
                     [showCurrentPageReport]="true"
                     currentPageReportTemplate="{first}–{last} von {totalRecords}"
                     (onLazyLoad)="onLazyLoad($event)">
                <ng-template pTemplate="header">
                    <tr>
                        <th class="w-3rem text-center">
                            <p-checkbox [ngModel]="allSelectableSelected"
                                        [binary]="true"
                                        [disabled]="selectableRows.length === 0"
                                        (onChange)="toggleSelectAll()">
                            </p-checkbox>
                        </th>
                        <th class="w-3rem"></th>
                        <th class="cursor-pointer" (click)="sortBy('kind')">
                            Art <i [class]="sortIcon('kind')"></i>
                        </th>
                        <th class="cursor-pointer" (click)="sortBy('partnerName')">
                            Gesellschaft <i [class]="sortIcon('partnerName')"></i>
                        </th>
                        <th>VSNR</th>
                        <th class="cursor-pointer text-right" (click)="sortBy('amount')">
                            Betrag <i [class]="sortIcon('amount')"></i>
                        </th>
                        <th class="cursor-pointer" (click)="sortBy('bookingPeriod')">
                            Buchungsperiode <i [class]="sortIcon('bookingPeriod')"></i>
                        </th>
                        <th>Status</th>
                        <th class="text-right">Aktion</th>
                    </tr>
                </ng-template>

                <ng-template pTemplate="body" let-row>
                    <tr [class.cn-row-focused]="isFocused(row)">
                        <td class="text-center">
                            @if (isOpen(row)) {
                                <p-checkbox [ngModel]="isSelected(row)"
                                            [binary]="true"
                                            (onChange)="toggleRowSelection(row)">
                                </p-checkbox>
                            }
                        </td>
                        <td class="text-center">
                            <p-button [icon]="isTraceExpanded(row) ? 'fa-regular fa-chevron-down' : 'fa-regular fa-chevron-right'"
                                      severity="secondary"
                                      size="small"
                                      [text]="true"
                                      [rounded]="true"
                                      pTooltip="Warum diese Zahl?"
                                      tooltipPosition="top"
                                      (onClick)="toggleTrace(row)">
                            </p-button>
                        </td>
                        <td>{{ kindLabel(row.kind) }}</td>
                        <td>{{ partnerLabel(row) }}</td>
                        <td>
                            <span class="text-sm">{{ vsnrLabel(row) }}</span>
                            <div class="text-xs text-600">{{ tariffLabel(row) }}</div>
                        </td>
                        <td class="text-right">
                            {{ (row.amount ?? 0) | currency:'EUR':'symbol':'1.2-2':'de' }}
                        </td>
                        <td>{{ row.bookingPeriod || '–' }}</td>
                        <td>
                            @if (isAcknowledged(row)) {
                                <p-tag severity="success" value="Quittiert"></p-tag>
                            } @else {
                                <p-tag severity="warn" value="Offen"></p-tag>
                            }
                        </td>
                        <td class="text-right">
                            @if (isOpen(row)) {
                                <p-button label="Quittieren"
                                          icon="fa-regular fa-check"
                                          severity="success"
                                          size="small"
                                          [disabled]="acknowledgeBusy"
                                          (onClick)="openConfirmSingle(row)">
                                </p-button>
                            } @else {
                                <span class="text-xs text-600">
                                    Bereits quittiert von {{ row.acknowledgedBy || 'Unbekannt' }}
                                    @if (row.acknowledgedAt) {
                                        am {{ row.acknowledgedAt | date:'dd.MM.yyyy':'':'de' }}
                                    }
                                </span>
                            }
                        </td>
                    </tr>

                    <!-- ===================== HERLEITUNG ("WARUM DIESE ZAHL?") ===================== -->
                    @if (isTraceExpanded(row)) {
                        <tr class="cn-trace-row">
                            <td colspan="9" class="p-3 surface-50">
                                <!-- VSNR / Tarif / Kunde / Gesellschaft -->
                                <div class="flex flex-wrap gap-4 mb-3">
                                    <div>
                                        <div class="text-xs text-600">VSNR</div>
                                        <div class="font-medium">{{ vsnrLabel(row) }}</div>
                                    </div>
                                    <div>
                                        <div class="text-xs text-600">Tarif</div>
                                        <div class="font-medium">{{ tariffLabel(row) }}</div>
                                    </div>
                                    <div>
                                        <div class="text-xs text-600">Kunde</div>
                                        <div class="font-medium">{{ customerLabel(row) }}</div>
                                    </div>
                                    <div>
                                        <div class="text-xs text-600">Gesellschaft</div>
                                        <div class="font-medium">{{ partnerLabel(row) }}</div>
                                    </div>
                                </div>

                                @if (traceState(row); as state) {
                                    @if (state.loading) {
                                        <p-skeleton height="6rem" width="100%"></p-skeleton>
                                    } @else if (state.trace) {
                                        <mvs-cn-expectation-trace
                                                [trace]="state.trace"
                                                [grossAmount]="state.grossAmount"
                                                [netAmount]="state.netAmount"
                                                [expanded]="true"
                                                title="Warum diese Zahl?">
                                        </mvs-cn-expectation-trace>
                                    } @else {
                                        <div class="text-600 p-3 text-center">
                                            <i class="fa-regular fa-circle-info mr-1"></i>
                                            Keine Herleitung verfügbar.
                                        </div>
                                    }
                                } @else {
                                    <p-skeleton height="6rem" width="100%"></p-skeleton>
                                }
                            </td>
                        </tr>
                    }
                </ng-template>

                <ng-template pTemplate="emptymessage">
                    <tr>
                        <td colspan="9" class="text-center text-600 p-4">
                            <i class="fa-regular fa-folder-open text-3xl mb-2 block"></i>
                            @if (searchTerm) {
                                Keine Treffer auf dieser Seite — die Suche filtert nur die aktuelle Seite.
                            } @else if (period || partnerId) {
                                Keine Daten für den gewählten Zeitraum.
                            } @else {
                                Keine Einträge gefunden.
                            }
                        </td>
                    </tr>
                </ng-template>
            </p-table>
            </div>

        <!-- ===================== QUITTIEREN-BESTÄTIGUNG ===================== -->
        <p-dialog [(visible)]="confirmVisible"
                  [header]="isBulkConfirm ? 'Vorgänge quittieren' : 'Vorgang quittieren'"
                  [modal]="true"
                  [closable]="!acknowledgeBusy"
                  [style]="{width: '32rem'}"
                  (onHide)="cancelConfirm()">

            @if (isBulkConfirm) {
                <!-- Bulk-Zusammenfassung: Anzahl + Summe -->
                <div class="mb-3">
                    <div class="text-sm text-600 mb-1">Sie quittieren mehrere Vorgänge:</div>
                    <div class="flex align-items-center justify-content-between surface-50 border-round p-3 mb-2">
                        <span class="font-medium">Anzahl Vorgänge</span>
                        <span class="text-xl font-bold">{{ confirmRows.length | number:'1.0-0':'de' }}</span>
                    </div>
                    <div class="flex align-items-center justify-content-between surface-50 border-round p-3">
                        <span class="font-medium">Summe</span>
                        <span class="text-xl font-bold">
                            {{ confirmSum | currency:'EUR':'symbol':'1.2-2':'de' }}
                        </span>
                    </div>
                </div>
            } @else if (confirmRows.length === 1) {
                <!-- Einzel-Zusammenfassung in Klartext -->
                <div class="mb-3">
                    <div class="text-sm text-600 mb-2">Möchten Sie diesen Vorgang quittieren?</div>
                    <div class="surface-50 border-round p-3">
                        <div class="flex justify-content-between py-1">
                            <span class="text-600">Art</span>
                            <span class="font-medium">{{ kindLabel(confirmRows[0].kind) }}</span>
                        </div>
                        <div class="flex justify-content-between py-1">
                            <span class="text-600">Gesellschaft</span>
                            <span class="font-medium">{{ partnerLabel(confirmRows[0]) }}</span>
                        </div>
                        <div class="flex justify-content-between py-1">
                            <span class="text-600">VSNR</span>
                            <span class="font-medium">{{ vsnrLabel(confirmRows[0]) }}</span>
                        </div>
                        <div class="flex justify-content-between py-1">
                            <span class="text-600">Betrag</span>
                            <span class="font-medium">
                                {{ (confirmRows[0].amount ?? 0) | currency:'EUR':'symbol':'1.2-2':'de' }}
                            </span>
                        </div>
                        <div class="flex justify-content-between py-1">
                            <span class="text-600">Periode</span>
                            <span class="font-medium">{{ confirmRows[0].bookingPeriod || '–' }}</span>
                        </div>
                    </div>
                </div>
            }

            <!-- 0,00 EUR — sanfte Warnung (FE-7) -->
            @if (zeroAmountWarning) {
                <p-message severity="warn" class="block mb-3"
                           text="Betrag ist 0,00 € — trotzdem quittieren?">
                </p-message>
            }

            <!-- Optionale Notiz -->
            <div class="mb-2">
                <label class="text-sm text-600 block mb-1" for="cn-ack-note">Notiz (optional)</label>
                <textarea id="cn-ack-note"
                          pTextarea
                          [(ngModel)]="confirmNote"
                          rows="3"
                          class="w-full"
                          placeholder="Kommentar zur Quittierung…">
                </textarea>
            </div>

            @if (acknowledgeError) {
                <p-message severity="error" class="block mt-2" [text]="acknowledgeError"></p-message>
            }

            <ng-template pTemplate="footer">
                <p-button label="Abbrechen"
                          icon="fa-regular fa-xmark"
                          severity="secondary"
                          [disabled]="acknowledgeBusy"
                          (onClick)="cancelConfirm()">
                </p-button>
                <p-button label="Ja, quittieren"
                          icon="fa-regular fa-check"
                          severity="success"
                          [loading]="acknowledgeBusy"
                          (onClick)="confirmAcknowledge()">
                </p-button>
            </ng-template>
        </p-dialog>

    </div>
}
